import { isValid } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import { PrdSectionsSummary } from './prd-sections-summary'
import { computeBodyStats } from '@/lib/utils/markdown-stats'
import { formatDateTime } from '@/lib/utils/date'
function safeFormatDateTime(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  return isValid(d) ? formatDateTime(value) : null
}

interface PrdDraftSidebarProps {
  // Minimal slice both BlogDraft and PrdDraft satisfy.
  draft: {
    body: string
    createdAt: string
    updatedAt: string
  }
  bodyOverride?: string
}

export function PrdDraftSidebar({ draft, bodyOverride }: PrdDraftSidebarProps) {
  // D-06: live-update content stats + sections summary from the in-progress
  // form body when editing; fall back to the saved state otherwise.
  // Timestamps intentionally stay bound to the SAVED server-side draft
  // (see Created / Updated rows below) — they describe the server state,
  // not the in-progress edit buffer.
  const body = bodyOverride ?? draft.body
  const stats = computeBodyStats(body)

  return (
    <div className="md:sticky md:top-4 md:self-start space-y-4">
      <PrdSectionsSummary body={body} />

      <Card>
        <CardContent className="p-4 space-y-2">
          <p className="text-sm font-medium mb-1">Content Stats</p>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Words</span>
            <span>{stats.words.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Paragraphs</span>
            <span>{stats.paragraphs}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Sentences</span>
            <span>{stats.sentences}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Reading time</span>
            <span>{stats.readingTimeMin} min</span>
          </div>
          {stats.headings > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Headings</span>
              <span>{stats.headings}</span>
            </div>
          )}
          {/* D-07: image count row is DELIBERATELY OMITTED for PRD sidebar. */}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Created</span>
            <span>{safeFormatDateTime(draft.createdAt) ?? '—'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Updated</span>
            <span>{safeFormatDateTime(draft.updatedAt) ?? '—'}</span>
          </div>
          {/* D-07: schedule and publish lifecycle rows are DELIBERATELY OMITTED.
               PRDs do not traverse approve → schedule → live in v4.2. */}
        </CardContent>
      </Card>
    </div>
  )
}
